import Chart, { ChartDataset, ChartData, ChartConfiguration, TooltipModel } from 'chart.js/auto'
import zoomPlugin from 'chartjs-plugin-zoom'
import { merge } from 'lodash'
import moment from 'moment'
import IWSChart, { ScaleOption } from '@/lib/graph/IWSChart/IWSChart'
import { getAxes } from '@/lib/graph/IWSChart/IWSAxes'
import { useInitDataStore } from '@/stores/initDataStore'
import { HistoryDataService } from '@/lib/graph/historyDataService'
import { XRange } from '@/lib/graph/types/initResult'

Chart.register(zoomPlugin)

type Scale = {
  min: number
  max: number
  autoStep: number
}

const DEFAULT_OPTIONS = {
  type: 'line',
  options: {
    responsive: true,
    aspectRatio: 2.7,
    interaction: {
      mode: 'x',
    },
    layout: {
      padding: {
        top: 0,
        right: 120,
        left: 50,
        bottom: 30,
      },
    },
    animation: {
      duration: 1,
    },
    plugins: {
      tooltip: {
        enabled: false,
        intersect: false,
      },
      legend: {
        labels: {
          usePointStyle: true,
        },
        display: false,
      },
    },
  },
  plugins: [
    {
      id: 'plugin',
    },
  ],
}

const LINE_STYLE = {
  pointRadius: 3,
  borderWidth: 2,
}

export default class IWSMainChart extends IWSChart {
  service: HistoryDataService

  constructor(
    public canvas: HTMLCanvasElement,
    public selectedFields: string[],
    public reportType: string,
    public scales?: { [key: string]: Scale[] }
  ) {
    super(canvas)
    this.service = new HistoryDataService()
  }

  render(data: ChartData) {
    const initDataStore = useInitDataStore()
    const threshold = initDataStore.initData.definitionInfo.graph.threshold
    const selectedField = this.selectedFields.length == 1 ? this.selectedFields[0] : undefined
    const units = this.selectedFields.map(f => initDataStore.getUnit(f))
    const scales = getAxes(true, this.selectedFields, this.reportType, units, threshold, selectedField)

    // APIからscaleの変更レスポンスがある場合上書き
    if (this.scales) {
      Object.keys(this.scales).forEach(_key => {
        const key = _key === '％' ? '%' : _key
        const newScale = this.scales?.[key]

        if (!newScale || !scales[key]) {
          return
        }

        // @ts-ignore
        scales[key].suggestedMin = Number(newScale.min)
        // @ts-ignore
        scales[key].suggestedMax = Number(newScale.max)
        // @ts-ignore
        scales[key].ticks.stepSize = Number(newScale.autoStep)
      })
    }

    const options = merge({}, DEFAULT_OPTIONS, {
      data,
      options: {
        scales,
        plugins: {
          tooltip: {
            external: (context: { chart: Chart; tooltip: TooltipModel<'line'> }) => {
              this.setToolTipContent(context)
            },
          },
        },
      },
      plugins: [
        {
          afterDatasetsDraw: this.getAfterDataDraw(),
          beforeDraw: this.beforeDraw,
        },
      ],
    }) as ChartConfiguration<'line'>

    this._render(options)
  }

  getAfterDataDraw() {
    return (c: Chart) => {
      const initDataStore = useInitDataStore()
      const initData = initDataStore.initData
      const ctx = c.ctx
      // グラフ上部のはみ出た+を白い四角形で上書き
      const top = c.chartArea.top
      const left = c.chartArea.left
      const right = c.chartArea.right
      const bottom = c.chartArea.bottom
      ctx.fillStyle = '#85929e'
      ctx.lineWidth = 1
      ctx.strokeRect(left, top, right - left, bottom - top)
      ctx.restore()
      const units = this.selectedFields.map(f => initDataStore.getUnit(f))
      const threshold = initData.definitionInfo.graph.threshold
      const selectedField = this.selectedFields.length == 1 ? this.selectedFields[0] : undefined
      const axes = getAxes(true, this.selectedFields, this.reportType, units, threshold, selectedField)

      Object.keys(c.scales ?? {}).map(key => {
        if (units.indexOf(key) >= 0) {
          const axis = axes[key] as ScaleOption[keyof ScaleOption]
          const position = this.getYAxisScalePosition(key)

          // y軸の上に単位を色付きで表示
          ctx.fillStyle = '#000000'
          ctx.font = '13px sans-serif'
          ctx.fillText(axis?.scaleLabel?.labelString as string, position.left, position.top + 10) // 上にはみ出るのでtop調整
          ctx.restore()
        }
      })
    }
  }

  beforeDraw(c: Chart) {
    const ctx = c.ctx
    const top = c.chartArea.top
    const left = c.chartArea.left
    const right = c.chartArea.right
    const bottom = c.chartArea.bottom

    ctx.fillStyle = '#ffffff'
    ctx.fillRect(left, top, right - left, bottom - top)
    ctx.restore()
  }

  setToolTipContent(context: { chart: Chart; tooltip: TooltipModel<'line'> }) {
    const { chart, tooltip } = context
    const tooltipEl = document.getElementById('tooltip-div') as HTMLElement
    const highlight = document.getElementById('x_highlight') as HTMLStyleElement

    tooltipEl.style.opacity = '0'
    highlight.style.opacity = '0'
    tooltipEl.innerHTML = ''

    if (tooltip.opacity === 0) {
      return
    }

    const type = this.reportType
    const titleLines = tooltip.title ?? []
    const initDataStore = useInitDataStore()
    const initData = initDataStore.initData

    // 局名と観測値をtooltip.body[i]から取り出す
    const bodyLines = tooltip.body
      .map(
        b =>
          b.lines.map((line: string) => {
            const [label, value] = line.split(':')
            const isWeatherGraph = this.selectedFields.length == 1
            const selectedField = isWeatherGraph ? this.selectedFields[0] : label
            const unit = this.service.getUnitFromFieldId(selectedField)
            const labelName = isWeatherGraph
              ? this.service.getStationNameFromStationId(label)
              : this.service.getFieldNameFromFieldId(label)
            const decimal = selectedField && initData.fieldList[selectedField]?.decimal
            const fixedValue = Number(value.replaceAll(',', '')).toFixed(decimal || 2)

            return { label: labelName, value: fixedValue, unit: unit }
          })[0]
      )
      .filter(line => !!line)

    const body = bodyLines

    // 日付設定
    titleLines.forEach((title: string) => {
      const tr = document.createElement('div')
      const time = moment(title, 'YYYY/MM/DD H:mm')
      switch (type) {
        case XRange.MONTH:
          title = time.format('YYYY/MM/DD')
          break
        case XRange.YEAR:
          title = time.format('YYYY/MM')
          break
      }
      const text = document.createTextNode(title)
      tr.appendChild(text)
      tooltipEl?.appendChild(tr)
    })

    // HTMLに整形
    body
      .sort((a, b) => (Number(a.value) < Number(b.value) ? 1 : -1))
      .forEach(body => {
        const tr = document.createElement('div')
        const text = `<div class="label">${body.label}</div><div><strong>${body.value}${
          body?.unit ?? ''
        }</strong></div>`

        tr.innerHTML = text
        tooltipEl?.appendChild(tr)
      })

    // tooltipEl / highlight の横位置調整
    const mainArea = chart.chartArea
    const mainChartRect = this.canvas.getBoundingClientRect()
    const { offsetLeft: positionX } = chart.canvas
    const left = positionX + tooltip.caretX
    // @ts-ignore
    const top = tooltip._eventPosition.y

    if (left > window.innerWidth) {
      return
    }

    tooltipEl.style.opacity = '1'
    tooltipEl.style.left = `${left}px`
    tooltipEl.style.top = `${top + tooltipEl.offsetHeight + 30}px`
    tooltipEl.style.padding = tooltip.options.padding + 'px '

    highlight.style.top = `${mainArea.top + (mainChartRect?.top ?? 0)}px`
    highlight.style.left = `${left - 1}px`
    highlight.style.height = `${mainArea.bottom - mainArea.top}px`
    highlight.style.opacity = '0.2'
  }

  getRange = (center: number, reportType?: string): { start: number; end: number } => {
    const type = reportType || this.reportType
    const time = center
    let start = moment(time)
    let end = moment(time)

    switch (type) {
      case XRange.HOUR:
        start = start.subtract(4, 'hour').startOf('hour')
        end = end.add(3, 'hour').endOf('hour')
        break
      case XRange.DAY:
        start = start.subtract(12, 'hour').startOf('hour')
        end = end.add(11, 'hour').endOf('hour')
        break
      case XRange.MONTH:
        start = start.subtract(15, 'day').startOf('day')
        end = end.add(15, 'day').endOf('day')
        break
      case XRange.YEAR:
        start = start.subtract(6, 'month').startOf('month')
        end = end.add(5, 'month').endOf('month').add(1, 'day')
        break
    }

    const startTime = start.toDate().getTime()
    const endTime = end.add(1, 'second').toDate().getTime()

    return {
      start: startTime,
      end: endTime,
    }
  }

  setData(_data: ChartDataset<'line'>[]) {
    const data: ChartDataset<'line'>[] = []

    _data.forEach(d => {
      data.push({ ...d, ...LINE_STYLE })
    })

    super.setData(data)
  }
}
